import { z } from "zod";

import { UserID } from "./hooks/useAccount";
import { RoleUtils } from "./utils/roleUtils";
import type { Satisfies } from "./utils/typeUtils";

export enum SubscriptionPeriod {
  Yearly = "yearly",
  Monthly = "monthly",
}

export enum SubscriptionBillingPeriod {
  Yearly = "yearly",
  Monthly = "monthly",
}

export enum SubscriptionTier {
  Pro = "pro",
  Lite = "lite",
}

export enum SubscriptionEffectiveFeatureSet {
  Pro = "pro",
}

export enum SubscriptionType {
  Gift = "gift",
  Edu = "edu",
  Team = "team",
  AppStore = "appstore",
  Paddle = "paddle",
  Chargebee = "chargebee",
}

export enum ServiceErrorCode {
  AppPropertiesNotFound = "APP_PROPERTIES_NOT_FOUND",
  AuthTokenExpired = "AUTH_TOKEN_EXPIRED",
  CaptchaMissmatch = "CAPTCHA_MISMATCH",
  CheckoutNotAllowed = "CHECKOUT_NOT_ALLOWED",
  EduRequestIneligible = "EDU_REQUEST_INELIGIBLE_EMAIL",
  EduRequestUnavailable = "EDU_REQUEST_UNAVAILABLE",
  EmailAlreadyUsed = "EMAIL_ALREADY_USED",
  EmailNotVerified = "EMAIL_NOT_VERIFIED",
  ForbiddenDomainForContributorInvitation = "FORBIDDEN_DOMAIN_FOR_CONTRIBUTOR_INVITATION",
  InsufficientSeats = "INSUFFICIENT_SEATS",
  InvalidAuthenticationSession = "INVALID_AUTHENTICATION_SESSION",
  InvalidConfirmationHash = "INVALID_CONFIRMATION_HASH",
  InvalidCoupon = "INVALID_COUPON",
  InvalidEmail = "INVALID_EMAIL",
  InvalidPassword = "PROJECT_VIEW_LINK_INVALID_PASSWORD",
  InvalidToken = "INVALID_TOKEN",
  InvalidUuid = "INVALID_UUID",
  MissingCaptcha = "MISSING_CAPTCHA",
  MultipleActiveSubscriptions = "MULTIPLE_ACTIVE_SUBSCRIPTIONS",
  OperationNotAllowed = "OPERATION_NOT_ALLOWED",
  PaddlePurchaseUnavailable = "PADDLE_PURCHASE_UNAVAILABLE",
  PasswordProtected = "PROJECT_VIEW_LINK_PASSWORD_PROTECTED",
  PasswordUnchanged = "PASSWORD_UNCHANGED",
  PaymentProcessingFailed = "PAYMENT_PROCESSING_FAILED",
  ProjectForbidden = "PROJECT_FORBIDDEN",
  ProjectNotFound = "PROJECT_NOT_FOUND",
  ProjectPendingAccess = "PENDING_PROJECT_ACCESS",
  SSOAuthenticationEmailMismatch = "SSO_AUTHENTICATION_EMAIL_MISMATCH",
  SSOAuthenticationSessionExpired = "SSO_AUTHENTICATION_SESSION_EXPIRED",
  SSOInvalidNameIDPolicy = "SSO_INVALID_NAME_ID_POLICY",
  SubscriptionOperationNotAllowed = "SUBSCRIPTION_OPERATION_NOT_ALLOWED",
  TeamInvitationAlreadyAccepted = "TEAM_INVITATION_ALREADY_ACCEPTED",
  TeamInvitationNotFound = "TEAM_INVITATION_NOT_FOUND",
  TeamInvitationNotSupported = "TEAM_INVITATION_NOT_SUPPORTED",
  TeamNotFound = "TEAM_NOT_FOUND",
  TooLongPassword = "TOO_LONG_PASSWORD",
  UnsupportedCountry = "UNSUPPORTED_COUNTRY",
  UserAlreadyHasSubscription = "USER_ALREADY_HAS_SUBSCRIPTION",
  UserAlreadyInThisTeam = "USER_ALREADY_IN_THIS_TEAM",
  UserAlreadyInvitedToTeam = "USER_ALREADY_INVITED_TO_TEAM",
  ValidationError = "VALIDATION_ERROR",
  WeakPassword = "WEAK_PASSWORD",
  WrongPassword = "WRONG_PASSWORD",
  TwoFactorTotpCodeRequired = "2FA_TOTP_CODE_REQUIRED",
}

export enum AuthError {
  InvalidEmailPassword,
  InvalidEmail,
  EmailAlreadyUsed,
  MissingCaptcha,
  PasswordUnchanged,
  RateLimit,
  WeakPassword,
  TooLongPassword,
  TOTPRequired,
  Unknown,
}

export const TierNames = new Map([
  ["pro", "Business"],
  ["lite", "Standard"],
]);

export type PriceConfig = Record<
  SubscriptionTier,
  Record<
    SubscriptionPeriod,
    {
      price: number;
      chinesePrice: number;
      discountPercent?: number;
      chineseDiscountPercent?: number;
    }
  >
>;

export const CurrencySymbols = new Map([
  ["USD", "$"],
  ["CNY", "¥"],
  ["MXN", "Mex$"],
  ["GBP", "£"],
  ["AUD", "AU$"],
  ["CAD", "CA$"],
  ["THB", "฿"],
  ["EUR", "€"],
]);

export type PaddleProductID = number;

export type SupportedLocale =
  | "de"
  | "en"
  | "es"
  | "fr"
  | "it"
  | "ja"
  | "ko"
  | "pt-BR"
  | "ru"
  | "zh"
  | "zh-hans"
  | "zh-hant";

export enum SearchParam {
  AuthHint = "auth_hint",
  EmailHint = "email_hint",
  ReturnTo = "return_to",
  RedirectUrl = "redirect_url",
  Source = "source",
  UTMSource = "utm_source",
  FromSurvey = "from_survey",
  AuthorizationCodeChallenge = "code_challenge",
  AuthorizationCode = "code",
  InvitationHash = "invitation_hash",
  EmailToken = "email_token",
  Hash = "hash",
  // App auth uses 'beta' string so I kept it for consistency but
  // it means beta app type not beta env so the enum name is BetaApp for clarification
  BetaApp = "beta",
  ProdApp = "prod",
  Signup = "signup",
  TrackingID = "tracking_id",
  DeviceID = "device_id",
  Purchase = "purchase",
  SubscriptionPeriod = "period",
  SubscriptionTier = "tier",
  SubscriptionCoupon = "coupon",
  SubscriptionCouponCode = "couponCode",
  CheckoutShowPeriodPicker = "show_checkout_period_picker",
  WithInvite = "withInvite",
  EditPaymentModalOpen = "edit_payment",
  FromAddSeats = "from_add_seats",
  PaymentUpdateSubscriptionID = "payment_update_subscription_id",
  InApp = "inapp",
  Layout = "layout",
  Theme = "theme",
  ShowPlans = "show_plans",
  IsTrial = "is_trial",
  CardError = "card_error",
  SegmentationSurvey = "segmentation",
  Intent = "intent",
  //eslint-disable-next-line
  SSOCode = "code",
  SSOState = "state",
  SSOError = "error",
  SSOErrorDescription = "error_description",
  SpaceID = "space_id",
}

export enum SearchParamIntent {
  Invite = "invite",
  Relogin = "relogin",
  RenameSpace = "rename_space",
  Share = "share",
}

export enum DeepLinkSearchParam {
  TeamID = "team",
  Environment = "env",
}

export enum AuthMethod {
  Password = "password",
  SSO = "sso",
}

export const URLStateActions = {
  OpenRenameSpaceModal: "open_rename_space_modal",
  OpenInviteModal: "open_invite_modal",
} as const;

type URLStateActionParamBase = {
  [key in keyof typeof URLStateActions]?: unknown;
};

type URLStateActionParams = Satisfies<
  {
    OpenRenameSpaceModal: {
      SpaceID: string;
    };
  },
  URLStateActionParamBase
>;

export type URLState = {
  [K in keyof typeof URLStateActions]: K extends keyof URLStateActionParams
    ? { Action: (typeof URLStateActions)[K]; Params: URLStateActionParams[K] }
    : { Action: (typeof URLStateActions)[K] };
}[keyof typeof URLStateActions];

// Order is important here, it affects the ranking of the roles it goes from most privileged to the least privileged
export const ProjectRoles = ["owner", "editor", "commenter", "viewer"] as const;
export type ProjectRole = (typeof ProjectRoles)[number];
export const ProjectRoleUtils = RoleUtils(ProjectRoles);

const ProjectRoleDisplayNames = {
  viewer: "Viewer",
  commenter: "Commenter",
  editor: "Editor",
  owner: "Owner",
} as const satisfies Record<ProjectRole, string>;

export const ProjectRoleDisplayName = (role: ProjectRole) => {
  return ProjectRoleDisplayNames[role];
};
export const ProjectRole = (role: string) => {
  if (!ProjectRoleUtils.validate(role)) {
    throw new Error(`Invalid project role: ${role}`);
  }
  return role as ProjectRole;
};

export const SpaceID = z.string().brand("SpaceID");
export type SpaceID = z.infer<typeof SpaceID>;

export const SpaceTypes = ["private", "team"] as const;
export type SpaceType = (typeof SpaceTypes)[number];

const SharingSpaceUserSchema = z.object({
  userId: UserID,
  name: z.string().optional(),
  email: z.string().email(),
});
type SharingSpaceUser = z.infer<typeof SharingSpaceUserSchema>;

export const SharingSpaceSchema = z.object({
  spaceId: SpaceID,
  spaceName: z.string().optional(),
  spaceType: z.enum(SpaceTypes),
  spaceUsers: z.array(SharingSpaceUserSchema).optional(),
});

export type SharingSpace =
  | {
      spaceId: SpaceID;
      spaceName?: string;
      spaceType: "private";
      spaceUsers?: SharingSpaceUser[];
    }
  | {
      spaceId: SpaceID;
      spaceName?: string;
      spaceType: "team";
      spaceUsers?: SharingSpaceUser[];
    };

export const ProjectID = z.string().brand("ProjectID");
export type ProjectID = z.infer<typeof ProjectID>;

export const RevisionID = z.number().nonnegative().brand("RevisionID");
export type RevisionID = z.infer<typeof RevisionID>;

export const ViewLinkID = z.string().brand("ViewLinkID");
export type ViewLinkID = z.infer<typeof ViewLinkID>;

export const TeamID = z.string().brand("TeamID");
export type TeamID = z.infer<typeof TeamID>;

export const WebExportID = z.string().brand("WebExportID");
export type WebExportID = z.infer<typeof WebExportID>;

export const DrawingID = z.string().brand("DrawingID");
export type DrawingID = z.infer<typeof DrawingID>;

export const FileDownloadSchema = z.object({
  method: z.enum(["GET", "POST"]),
  url: z.string().url(),
  headers: z.record(z.string()).optional(),
  expiry: z.coerce.date(),
});
